import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Clock, ArrowUp, ArrowDown, CircleDot, Sunrise, Moon, Activity, Target, Zap, Waves } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { EnergyBadge } from "@/components/meeting/EnergyBadge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  getEnergyBlock, 
  getEnergyForecastSync, 
  mockWearableData
} from '@/lib/energy';
import { getUserEnergySettings, parseTimeToHours } from '@/lib/energy/user-settings';
import { formatSecondsToAMPM } from '@/lib/time-utils';
import { UserEnergySettings } from '@/lib/energy/types';
import { Task, energyTasks } from '@/lib/tasks';
import { EnhancedEnergyChart } from './energy/EnhancedEnergyChart';
import { useWearableAverages } from '@/hooks/use-wearable-averages';
import { useHealthyWearableConnections } from '@/hooks/use-healthy-wearable-connections';

function getRandomTasks<T extends Task>(tasks: T[] | undefined, count: number): T[] {
  if (!tasks || tasks.length === 0) {
    return [];
  }
  
  if (tasks.length <= count) return tasks;
  const shuffled = [...tasks].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

function EnergyForecastSidebarCardComponent() {
  const [now, setNow] = useState(new Date());
  const [useWearableData, setUseWearableData] = useState(false); 
  const [userSettings, setUserSettings] = useState<UserEnergySettings | null>(null);
  const { data: wearableAverages } = useWearableAverages();
  const { hasHealthyConnection } = useHealthyWearableConnections();

  // Timer effect - updates every 1 minute
  useEffect(() => {
    // Update immediately on mount
    setNow(new Date());
    
    const timer = setInterval(() => {
      const newTime = new Date();
      setNow(newTime);
      // Removed excessive timer logging
    }, 60000); // 1 minute = 60000ms
    
    return () => clearInterval(timer);
  }, []);

  // Memoized fetch settings function
  const fetchSettings = useCallback(async () => {
    const settings = await getUserEnergySettings();
    setUserSettings(settings);
    // Removed excessive settings logging
  }, []);

  // Fetch user settings
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);
  
  // Default or user settings
  const wakeTime = userSettings ? parseTimeToHours(userSettings.wakeTime) : 7;
  const sleepTime = userSettings ? parseTimeToHours(userSettings.sleepTime) : 22;
  const sleepDuration = sleepTime > wakeTime ? 24 - (sleepTime - wakeTime) : 24 - (wakeTime - sleepTime);

  // Get energy forecast using consistent parameters - USE SYNC VERSION
  const energyForecast = useMemo(() => {
    return getEnergyForecastSync(
      { wakeTimeHour: wakeTime, sleepDuration, useShiftedCurve: true },
      useWearableData ? mockWearableData : undefined,
      0,
      userSettings
    );
  }, [wakeTime, sleepTime, sleepDuration, useWearableData, userSettings]);
  
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();
  
  // Get current energy level with interpolation
  let currentEnergy;
  if (energyForecast && energyForecast[currentHour] !== undefined) {
    const currentHourEnergy = energyForecast[currentHour];
    const nextHourEnergy = energyForecast[(currentHour + 1) % 24] || currentHourEnergy;
    const progress = currentMinutes / 60;
    currentEnergy = currentHourEnergy + (nextHourEnergy - currentHourEnergy) * progress;
  } else {
    currentEnergy = 50;
  }
  
  // Get the current energy zone based on interpolated energy level
  const currentBlock = getEnergyBlock(currentEnergy);
  
  // Removed excessive debug logging for current energy calculation
  
  // Memoized next energy zone calculation
  const getNextEnergyZone = useCallback(() => {
    if (!energyForecast || !currentBlock) {
      return {
        nextBlock: "Focus Zone",
        hoursLeft: 1,
        minutesLeft: 0
      };
    }
    
    // Check every minute for the next 2 hours
    for (let minutesFromNow = 1; minutesFromNow <= 120; minutesFromNow++) {
      const futureTime = new Date(now.getTime() + minutesFromNow * 60000);
      const futureHour = futureTime.getHours();
      const futureMinutes = futureTime.getMinutes();
      
      // Get energy level at future time with proper interpolation
      let futureEnergy;
      if (futureHour === currentHour) {
        // Within same hour, interpolate between current and next hour
        const currentEnergy = energyForecast[currentHour] || 50;
        const nextHourEnergy = energyForecast[(currentHour + 1) % 24] || 50;
        const progress = futureMinutes / 60;
        futureEnergy = currentEnergy + (nextHourEnergy - currentEnergy) * progress;
      } else {
        // Different hour, interpolate between that hour and next hour
        const hour1Energy = energyForecast[futureHour] || 50;
        const hour2Energy = energyForecast[(futureHour + 1) % 24] || 50;
        const progress = futureMinutes / 60;
        futureEnergy = hour1Energy + (hour2Energy - hour1Energy) * progress;
      }
      
      const futureBlockType = getEnergyBlock(futureEnergy).type;
      
      // Removed excessive debug logging for iterations
      
      if (futureBlockType !== currentBlock.type) {
        const hoursLeft = Math.floor(minutesFromNow / 60);
        const minutesLeft = minutesFromNow % 60;
        
        // Only log when block actually changes
        console.log(`EnergyForecastSidebarCard: Block change detected at ${minutesFromNow}m - ${futureBlockType} (${hoursLeft}h ${minutesLeft}m)`);
        
        return {
          nextBlock: futureBlockType === "power" ? "Power Zone" : 
                    futureBlockType === "focus" ? "Focus Zone" : "Drift Zone",
          hoursLeft,
          minutesLeft
        };
      }
    }
    
    return {
      nextBlock: "Power Zone",
      hoursLeft: 1,
      minutesLeft: 30
    };
  }, [energyForecast, currentBlock, currentHour, now]);
  
  const nextBlockInfo = getNextEnergyZone();
  
  const tasksToShow = useMemo(() => {
    if (!energyTasks) {
      console.warn("EnergyForecastSidebarCard: energyTasks is undefined");
      return [];
    }
    
    if (!currentBlock || !currentBlock.type || !energyTasks[currentBlock.type]) {
      console.warn(`EnergyForecastSidebarCard: No tasks found for energy zone type: ${currentBlock?.type}`, {
        availableTypes: Object.keys(energyTasks),
        currentBlockType: currentBlock?.type
      });
      return [];
    }
    
    return getRandomTasks(energyTasks[currentBlock.type], 3);
  }, [currentBlock?.type]);

  // Memoized format time label function
  const formatTimeLabel = useCallback((hour: number) => {
    return hour === 0 ? '12am' : 
           hour < 12 ? `${hour}am` : 
           hour === 12 ? '12pm' : 
           `${hour - 12}pm`;
  }, []);

  const formatTimestampToAMPM = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  };

  // Custom tooltip component for task suggestions
  const TaskSuggestionsTooltip = ({ tasks, energyType }: { tasks: Task[], energyType: string }) => {
    const getEnergyIcon = () => {
      switch (energyType?.toLowerCase()) {
        case "power":
          return <Zap className="h-4 w-4 text-green-600" />;
        case "focus":
          return <Target className="h-4 w-4 text-blue-600" />;
        case "drift":
          return <Waves className="h-4 w-4 text-purple-600" />;
        default:
          return <Target className="h-4 w-4 text-blue-600" />;
      }
    };

    const getEnergyColor = () => {
      switch (energyType?.toLowerCase()) {
        case "power":
          return "border-green-200 bg-green-50";
        case "focus":
          return "border-blue-200 bg-blue-50";
        case "drift":
          return "border-purple-200 bg-purple-50";
        default:
          return "border-blue-200 bg-blue-50";
      }
    };

    const getEnergyDescription = () => {
      switch (energyType?.toLowerCase()) {
        case "power":
          return "High energy period - perfect for high-impact activities, presentations, and strategic decisions.";
        case "focus":
          return "Moderate energy period - ideal for analytical work, planning, and detailed tasks.";
        case "drift":
          return "Lower energy period - great for creative thinking, collaboration, and lighter activities.";
        default:
          return "Current energy level - optimize your productivity with these task suggestions.";
      }
    };

    return (
      <div className={`p-4 rounded-lg border ${getEnergyColor()} max-w-xs`}>
        {/* Energy Zone Description */}
        <div className="mb-3">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {getEnergyDescription()}
          </p>
        </div>

        {/* Ideal Tasks Section */}
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-2">
            {getEnergyIcon()}
            <h4 className="font-semibold text-sm">Ideal tasks</h4>
          </div>
          
          {/* Task Bullet Points */}
          <div className="space-y-1.5">
            {tasks.map((task, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-current mt-2 flex-shrink-0" />
                <div className="text-sm font-medium">{task.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Hint */}
        <div className="pt-2 border-t border-white/30">
          <p className="text-xs text-muted-foreground">
            💡 Hover over your energy badge anytime to see task suggestions
          </p>
        </div>
      </div>
    );
  };

  // Check if energy data is available
  if (!energyForecast || energyForecast.length === 0) {
    return (
      <Link to="/energy">
        <Card className="mx-2 border border-none shadow-sm hover:border-muted-400/50 transition-colors rounded-sm">
          <CardContent className="flex flex-col gap-1 py-4 px-4">
            <h3 className="font-medium text-sm mb-0">Today's Energy Forecast</h3>
            <p className="text-sm text-muted-foreground">Loading your energy data...</p>
          </CardContent>
        </Card>
      </Link>
    );
  }

  return (
    <Link to="/energy">
      <Card className="mx-0 border border-none shadow-none">
        <CardContent className="flex flex-col gap-1 p-4">
          {/* <h3 className="font-medium text-sm mb-2">Energy Forecast</h3> */}
          <div className={`flex items-center gap-2 text-md font-semibold ${currentBlock.color}`}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-help hover:scale-105 transition-transform duration-200">
                    <EnergyBadge energyPreference={currentBlock.type} />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="p-0 border-0 bg-transparent">
                  {tasksToShow.length > 0 ? (
                    <TaskSuggestionsTooltip tasks={tasksToShow} energyType={currentBlock.type} />
                  ) : (
                    <div className="p-4 rounded-lg border border-blue-200 bg-blue-50 max-w-xs">
                      {/* Energy Zone Description */}
                      <div className="mb-3">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {currentBlock.type === 'power' ? "High energy period. Perfect for high-impact activities, presentations, and strategic decisions." :
                           currentBlock.type === 'focus' ? "Moderate energy period. Ideal for analytical work, planning, and detailed tasks." :
                           currentBlock.type === 'drift' ? "Lower energy period. Great for creative thinking, collaboration, and lighter activities." :
                           "Current energy level - optimize your productivity with these task suggestions."}
                        </p>
                      </div>

                      {/* Ideal Tasks Section */}
                      <div className="mb-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="h-4 w-4 text-blue-600" />
                          <h4 className="font-semibold text-sm">Ideal tasks</h4>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          No specific tasks available at the moment
                        </div>
                      </div>

                      {/* Hint */}
                      <div className="pt-2 border-t border-white/30">
                        <p className="text-xs text-muted-foreground">
                          💡 Check back later for personalized task suggestions
                        </p>
                      </div>
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-xs text-zinc-700 mt-1 mb-0">{currentBlock.description}</p>
          
          <div className="my-1 -mx-3">
            <EnhancedEnergyChart 
              energyForecast={energyForecast}
              height={100}
              showCurrentTime={true}
              enableTooltip={false}
              simplified={true}
              wakeTimeHour={wakeTime}
            />
          </div>
          
          <div className="flex justify-between text-xs text-muted-foreground mt-0">
            <div className="flex items-center gap-1">
              <Sunrise className="h-3 w-3 text-amber-500" />
              <span>{formatTimeLabel(Math.floor(wakeTime))}</span>
            </div>
            <div className="flex items-center gap-1">
              <Moon className="h-3 w-3 text-blue-500" />
              <span>{formatTimeLabel(Math.floor(sleepTime))}</span>
            </div>
          </div>

          {/* Wearable Averages Section */}
          {wearableAverages?.hasEnoughData && hasHealthyConnection && (
            <div className="flex items-start gap-2 mt-2 p-2 bg-muted/50 rounded-md">
              <Activity className="h-3 w-3 text-green-500 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium mb-1">Avg. Wearable Data</div>
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">
                    Sleep: {wearableAverages.avgSleepDuration.toFixed(1)}h • HRV: {wearableAverages.avgHRV.toFixed(0)}
                  </div>
                  {(wearableAverages.avgSleepOnset != null || wearableAverages.avgWakeupTime != null) && (
                    <div className="text-xs text-muted-foreground">
                      {wearableAverages.avgSleepOnset != null && (
                        <>{formatSecondsToAMPM(wearableAverages.avgSleepOnset)}</>
                      )}
                      {wearableAverages.avgSleepOnset != null && wearableAverages.avgWakeupTime != null && (
                        <> to </>
                      )}
                      {wearableAverages.avgWakeupTime != null && (
                        <>{formatSecondsToAMPM(wearableAverages.avgWakeupTime)}</>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Next Block Info */}
          <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {((nextBlockInfo.hoursLeft === 0 && nextBlockInfo.minutesLeft < 15)) ? (
              <span>
                Next: <strong>{nextBlockInfo.nextBlock}</strong> in {nextBlockInfo.minutesLeft}m
              </span>
            ) : (
              <span>
                {nextBlockInfo.hoursLeft > 0 && (
                  <>
                    {nextBlockInfo.hoursLeft}h{" "}
                  </>
                )}
                {nextBlockInfo.minutesLeft > 0 && (
                  <>
                    {nextBlockInfo.minutesLeft}m{" "}
                  </>
                )}
                left of {currentBlock.type === 'power' ? 'Power Zone' : currentBlock.type === 'focus' ? 'Focus Zone' : 'Drift Zone'}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// Export memoized component to prevent unnecessary re-renders
export const EnergyForecastSidebarCard = React.memo(EnergyForecastSidebarCardComponent);
